import { io } from 'socket.io-client';
import SocketManager from './SocketManager';
import { MODULES } from 'figshelf-modules';

// Global interceptor to catch ALL socket.io connections in web client
const originalIo = window.io || require('socket.io-client').io;
window.io = function(...args) {
  console.log('🌐 WEB CLIENT: Global socket.io interceptor - Connection attempt detected!', {
    args: args,
    stackTrace: new Error().stack,
    timestamp: new Date().toISOString()
  });
  
  // Check if user is authenticated before allowing any connection
  try {
    const token = localStorage.getItem('@auth_token');
    if (!token || token.split('.').length !== 3) {
      console.warn('⚠️ GLOBAL AUTH CHECK: No valid authentication token found - skipping socket connection');
      // Return a mock socket that does nothing
      return {
        on: () => {},
        off: () => {},
        emit: () => {},
        disconnect: () => {},
        close: () => {},
        connect: () => {},
        connected: false,
        disconnected: true
      };
    }
  } catch (error) {
    console.warn('⚠️ GLOBAL AUTH CHECK: Authentication check failed - skipping socket connection:', error.message);
    // Return a mock socket that does nothing
    return {
      on: () => {},
      off: () => {},
      emit: () => {},
      disconnect: () => {},
      close: () => {},
      connect: () => {},
      connected: false,
      disconnected: true
    };
  }
  
  // Check if this is a managed connection by looking at the stack trace
  const stackTrace = new Error().stack;
  const isManagedConnection = stackTrace.includes('SocketService.connect') || 
                           stackTrace.includes('SocketService.js');
  
  if (!isManagedConnection) {
    console.error('🚫 WEB CLIENT: Blocking unmanaged socket connection!', {
      args: args,
      stackTrace: stackTrace,
      timestamp: new Date().toISOString()
    });
    throw new Error('Unmanaged socket connection blocked - use SocketService instead');
  }
  
  return originalIo.apply(this, args);
};
window.io.connect = window.io;

const { Constants } = MODULES;
const { constants } = Constants;

export const NAMESPACES = {
  NOTIFICATIONS: 'notifications',
  MESSAGES: 'messages',
  USER: 'user',
  UNIVERSAL: 'universal',
  DEFAULT: 'default'
};

export class SocketRequest {
  constructor(
    socketId,
    conversationId,
    event,
    content
  ) {
    this.socketId = socketId;
    this.conversationId = conversationId;
    this.notificationId = conversationId;
    this.event = event;
    this.content = content;
  }  
  
  toObject() {
    return {
      socketId: this.socketId,
      conversationId: this.conversationId,
      event: this.event,
      content: this.content
    };
  }

  static fromObject(obj) {
    if (typeof obj === "string") {
      obj = JSON.parse(obj);
    }
    return new SocketRequest(
      obj.socketId, 
      obj.conversationId || obj.notificationId, 
      obj.event, 
      obj.content
    );
  }
}

class SocketService {
  static instances = new Map();

  constructor(namespace = NAMESPACES.DEFAULT) {
    if (SocketService.instances.has(namespace)) {
      return SocketService.instances.get(namespace);
    }

    this.socket = null;
    this.namespace = namespace;
    this.isConnected = false;
    this.pendingEmits = [];
    this.connectionPromise = null;
    this.eventListeners = new Map();
    this.connectionTimeout = null;
    this.lastPingTime = null;
    
    // Enhanced real-time features
    this.connectionQuality = 'unknown'; // 'excellent', 'good', 'poor', 'disconnected'
    this.latency = 0;
    this.reconnectAttempts = 0;
    this.maxReconnectAttempts = 10;
    this.reconnectDelay = 1000; // Start with 1 second
    this.maxReconnectDelay = 30000; // Max 30 seconds
    this.heartbeatInterval = null;
    this.connectionStateCallbacks = new Set();
    this.isReconnecting = false;
    
    SocketService.instances.set(namespace, this);
    
    // Register with SocketManager
    const socketManager = SocketManager.getInstance();
    socketManager.registerSocket(namespace, this);
  }

  async getToken() {
    try {
      // Use same token key as APIService
      const token = localStorage.getItem('@auth_token');
      
      if (!token) {
        console.warn(`⚠️ No authentication token found in localStorage`);
        throw new Error('No authentication token available');
      }
      
      // Basic token validation (JWT should have 3 parts separated by dots)
      const tokenParts = token.split('.');
      if (tokenParts.length !== 3) {
        console.warn(`⚠️ Invalid token format: Expected 3 parts, got ${tokenParts.length}`);
        throw new Error('Invalid token format');
      }
      
      return token;
    } catch (error) {
      console.error('Failed to get token:', error);
      throw error;
    }
  }

  async connect() {
    // Global check: Don't allow any connections before authentication

    try {
      const token = await this.getToken();
      if (!token) {
        console.warn(`⚠️ No authentication token available for ${this.namespace} - skipping connection`);
        throw new Error('No authentication token available');
      }
    } catch (error) {
      console.warn(`⚠️ Authentication check failed for ${this.namespace}:`, error.message, '- skipping connection');
      throw error;
    }

    if (this.connectionPromise) {
      return this.connectionPromise;
    }

    this.connectionPromise = new Promise(async (resolve, reject) => {
      try {
        // Token already validated at the beginning of connect()
        const token = await this.getToken();

        // Create socket connection using constants
        const socketUrl = constants.SOCKET_URL;
        
        // If SOCKET_URL is relative (starts with /), use API_URL as base
        const fullSocketUrl = socketUrl.startsWith('/') 
          ? `${constants.API_URL}${socketUrl}`
          : socketUrl;
        
        this.socket = io(fullSocketUrl, {
          auth: {
            token: token
          },
          query: {
            namespace: this.namespace
          },
          transports: ['polling', 'websocket'], // Start with polling to avoid WebSocket errors on initial connect
          reconnection: true,
          reconnectionAttempts: 5, // Limited attempts instead of Infinity
          reconnectionDelay: 1000,
          reconnectionDelayMax: 5000,
          randomizationFactor: 0.5,
          timeout: 20000,
          autoConnect: true,
        });

        // Suppress error events from bubbling to React error overlay
        this.socket.io.on('error', (error) => {
          console.warn(`Socket.io engine error for ${this.namespace} (suppressed):`, error?.message || error);
        });

        // Set up connection timeout
        this.connectionTimeout = setTimeout(() => {
          if (!this.isConnected) {
            const error = new Error('Connection timeout');
            this.socket?.close();
            this.connectionPromise = null;
            reject(error);
          }
        }, 20000);

        // Handle successful connection
        this.socket.on('connect', () => {
          this.isConnected = true;
          this.isReconnecting = false;
          this.reconnectAttempts = 0;
          this.reconnectDelay = 1000; // Reset delay
          this.connectionQuality = 'good';
          clearTimeout(this.connectionTimeout);
          
          console.log(`✅ Socket connected for ${this.namespace}`);
          
          // Start heartbeat monitoring
          this.startHeartbeat();
          
          // Process any pending emits
          while (this.pendingEmits.length > 0) {
            const { event, data } = this.pendingEmits.shift();
            this.socket.emit(event, data);
          }
          
          // Notify connection state callbacks
          this.notifyConnectionState('connected');

          resolve();
        });

        // Handle connection error
        this.socket.on('connect_error', (error) => {
          console.error(`❌ Socket connection error for ${this.namespace}:`, error);
          this.isConnected = false;
          clearTimeout(this.connectionTimeout);
          this.connectionPromise = null;
          
          // If it's an authentication error, disable reconnection
          if (error.message?.includes('jwt malformed') || 
              error.message?.includes('No authentication token') ||
              error.message?.includes('Invalid token format') ||
              error.message?.includes('Authentication failed')) {
            console.warn(`🚫 Authentication error for ${this.namespace}, disabling reconnection`);
            this.socket.io.opts.reconnection = false;
            this.socket.disconnect();
          }
          
          reject(error);
        });

        // Handle disconnection
        this.socket.on('disconnect', (reason) => {
          this.isConnected = false;
          this.connectionPromise = null;
          this.connectionQuality = 'disconnected';
          
          // Stop heartbeat
          this.stopHeartbeat();
          
          console.log(`🔌 Socket disconnected for ${this.namespace}. Reason: ${reason}`);
          
          // Notify connection state callbacks
          this.notifyConnectionState('disconnected', reason);

          // Only notify SocketManager for unexpected disconnections
          if (reason === 'io server disconnect' || reason === 'transport close' || reason === 'transport error') {
            console.log(`⚠️ Unexpected disconnection for ${this.namespace}, will attempt reconnect...`);
            const socketManager = SocketManager.getInstance();
            socketManager.handleUnexpectedDisconnection(this.namespace);
          }
        });

        // Handle reconnection attempt
        this.socket.io.on('reconnect_attempt', (attemptNumber) => {
        });

        // Handle successful reconnection
        this.socket.io.on('reconnect', (attemptNumber) => {
          this.isConnected = true;
          this.isReconnecting = false;
          this.reconnectAttempts = 0;
          this.connectionQuality = 'good';
          
          console.log(`🔄 Socket reconnected for ${this.namespace} after ${attemptNumber} attempts`);
          
          // Start heartbeat monitoring
          this.startHeartbeat();
          
          // Re-register all event listeners
          this.eventListeners.forEach((listeners, event) => {
            listeners.forEach(callback => {
              this.socket.on(event, callback);
            });
          });
          
          // Process any pending emits
          while (this.pendingEmits.length > 0) {
            const { event, data } = this.pendingEmits.shift();
            this.socket.emit(event, data);
          }
          
          // Notify connection state callbacks
          this.notifyConnectionState('reconnected', { attemptNumber });
        });

        // Handle reconnection error
        this.socket.io.on('reconnect_error', (error) => {
        });

        // Handle reconnection failed
        this.socket.io.on('reconnect_failed', () => {
        });

        // Set up ping/pong handling for latency monitoring
        this.socket.on('ping', () => {
          this.lastPingTime = Date.now();
        });

        this.socket.on('pong', (data) => {
          const now = Date.now();
          if (this.lastPingTime) {
            this.latency = now - this.lastPingTime;
            this.updateConnectionQuality();
          }
          this.lastPingTime = now;
        });

      } catch (error) {
        console.error(`❌ Failed to connect socket for ${this.namespace}:`, error);
        clearTimeout(this.connectionTimeout);
        this.connectionPromise = null;
        reject(error);
      }
    });

    return this.connectionPromise;
  }

  async disconnect() {
    
    // Clear connection timeout
    if (this.connectionTimeout) {
      clearTimeout(this.connectionTimeout);
      this.connectionTimeout = null;
    }

    if (this.socket) {
      // Remove all listeners
      this.eventListeners.forEach((listeners, event) => {
        listeners.forEach(callback => {
          this.socket.off(event, callback);
        });
      });
      this.eventListeners.clear();

      // Close socket
      this.socket.close();
      this.socket = null;
    }

    this.isConnected = false;
    this.connectionPromise = null;
    
    // Unregister from SocketManager
    const socketManager = SocketManager.getInstance();
    socketManager.unregisterSocket(this.namespace);
    
    // Remove from instances map
    SocketService.instances.delete(this.namespace);
    
  }

  emit(event, data) {
    if (this.isConnected && this.socket && this.socket.connected) {
      return this.socket.emit(event, data);
    } else {
      this.pendingEmits.push({ event, data });
      
      // Try to connect if not connected
      if (!this.connectionPromise) {
        this.connect().catch(error => {
          console.error(`Failed to connect while trying to emit ${event}:`, error);
        });
      }
    }
  }

  on(event, callback) {
    // Store the callback for reconnection
    if (!this.eventListeners.has(event)) {
      this.eventListeners.set(event, new Set());
    }
    
    // Check if this exact callback is already registered
    const listeners = this.eventListeners.get(event);
    if (listeners.has(callback)) {
      console.warn(`⚠️ Duplicate listener registration for event "${event}"`);
      return () => this.off(event, callback);
    }
    
    listeners.add(callback);
    
    if (!this.socket) {
      return () => this.off(event, callback);
    }
    
    this.socket.on(event, callback);
    
    // Return cleanup function
    return () => this.off(event, callback);
  }
  
  off(event, callback) {
    if (this.eventListeners.has(event)) {
      this.eventListeners.get(event).delete(callback);
    }
    if (this.socket) {
      this.socket.off(event, callback);
    }
  }

  /**
   * Get connection statistics
   */
  getStats() {
    return {
      namespace: this.namespace,
      isConnected: this.isConnected,
      pendingEmits: this.pendingEmits.length,
      registeredEvents: Array.from(this.eventListeners.keys()),
      lastPingTime: this.lastPingTime,
    };
  }

  /**
   * Start heartbeat monitoring for the socket connection
   */
  startHeartbeat() {
    // Clear any existing interval
    this.stopHeartbeat();
    
    // Set up heartbeat interval (every 25 seconds)
    this.heartbeatInterval = setInterval(() => {
      if (this.socket && this.socket.connected) {
        this.socket.emit('ping', { timestamp: Date.now() });
      }
    }, 25000);
  }

  /**
   * Stop the heartbeat monitoring
   */
  stopHeartbeat() {
    if (this.heartbeatInterval) {
      clearInterval(this.heartbeatInterval);
      this.heartbeatInterval = null;
    }
  }

  /**
   * Update connection quality based on latency
   */
  updateConnectionQuality() {
    if (this.latency < 100) {
      this.connectionQuality = 'excellent';
    } else if (this.latency < 300) {
      this.connectionQuality = 'good';
    } else if (this.latency < 1000) {
      this.connectionQuality = 'poor';
    } else {
      this.connectionQuality = 'disconnected';
    }
  }

  /**
   * Notify all registered connection state callbacks
   */
  notifyConnectionState(state, data = {}) {
    this.connectionStateCallbacks.forEach(callback => {
      try {
        callback({
          namespace: this.namespace,
          state,
          ...data
        });
      } catch (error) {
        console.error('Error in connection state callback:', error);
      }
    });
  }

  /**
   * Schedule reconnection with exponential backoff
   */
  scheduleReconnect() {
    if (this.isReconnecting) {
      return;
    }

    this.isReconnecting = true;
    this.reconnectAttempts++;

    if (this.reconnectAttempts > this.maxReconnectAttempts) {
      console.error(`❌ Max reconnection attempts (${this.maxReconnectAttempts}) reached for ${this.namespace}`);
      this.isReconnecting = false;
      this.notifyConnectionState('failed', { reason: 'Max reconnection attempts reached' });
      return;
    }

    // Calculate delay with exponential backoff
    const delay = Math.min(
      this.reconnectDelay * Math.pow(2, this.reconnectAttempts - 1),
      this.maxReconnectDelay
    );

    console.log(`🔄 Scheduling reconnection attempt ${this.reconnectAttempts}/${this.maxReconnectAttempts} for ${this.namespace} in ${delay}ms`);

    setTimeout(() => {
      if (this.isReconnecting && !this.isConnected) {
        console.log(`🔄 Attempting to reconnect ${this.namespace}...`);
        this.connect()
          .then(() => {
            console.log(`✅ Reconnection successful for ${this.namespace}`);
            this.isReconnecting = false;
          })
          .catch((error) => {
            console.error(`❌ Reconnection failed for ${this.namespace}:`, error);
            this.scheduleReconnect();
          });
      }
    }, delay);
  }
}

export default SocketService;